import React, { useState, useMemo } from 'react';
import { Table, Button, Icon, Label } from 'semantic-ui-react';
import * as XLSX from 'xlsx';

const SortableTable = ({ collectionsList, formatCurrency, getPracticeName }) => {
    const [sortConfig, setSortConfig] = useState({ column: null, direction: null });

    // Flatten the nested collectionsList into rows
    const collectionsTableRows = [];
    collectionsList.forEach((practiceCollectionsList) => {
        practiceCollectionsList.forEach((dailyCollectionsList) => {
            dailyCollectionsList.forEach((dailyCollection) => {
                collectionsTableRows.push(dailyCollection);
            });
        });
    });

    // Sorting logic
    const handleSort = (column) => {
        let direction = 'ascending';
        if (sortConfig.column === column && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ column, direction });
    };

    const sortedRows = useMemo(() => {
        return [...collectionsTableRows].sort((a, b) => {
            if (sortConfig.column) {
                const aValue = a[sortConfig.column];
                const bValue = b[sortConfig.column];
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
    }, [sortConfig, collectionsTableRows]);

    // Calculate total for the "Amount" column
    const totalAmount = useMemo(() => {
        return collectionsTableRows.reduce((total, row) => total + (row.Amount || 0), 0);
    }, [collectionsTableRows]);

    const tedTotalAmount = useMemo(() => {
        return collectionsTableRows.reduce((total, row) => total + (row.isTedDay ? (row.Amount || 0) : 0), 0);
    }, [collectionsTableRows]);

    const tedMonthlyTotals = useMemo(() => {
        const totalsByMonth = {};
        collectionsTableRows.forEach((row) => {
            if (row.isTedDay && row.ProcDate) {
                const monthKey = row.ProcDate.slice(0, 7);
                totalsByMonth[monthKey] = (totalsByMonth[monthKey] || 0) + (row.Amount || 0);
            }
        });
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return Object.keys(totalsByMonth).sort().map((monthKey) => {
            const [year, month] = monthKey.split('-');
            return {
                monthKey,
                label: `${monthNames[parseInt(month, 10) - 1]} ${year}`,
                amount: totalsByMonth[monthKey]
            };
        });
    }, [collectionsTableRows]);

    const handleExportToExcel = () => {
        const exportRows = sortedRows.map(row => ({
            'Procedure Date': row.ProcDate,
            'Entry Date': row.EntryDate,
            'Practice': getPracticeName ? getPracticeName(row.Practice) : row.Practice,
            'TED': row.isTedDay ? 'Yes' : 'No',
            'Patient Number': row.PatNum,
            'Amount': row.Amount || 0
        }));
        exportRows.push({
            'Procedure Date': '', 'Entry Date': '', 'Practice': '', 'TED': '', 'Patient Number': '',
            'Amount': totalAmount
        });
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Collections');
        XLSX.writeFile(workbook, `collections-records-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // Render the table
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                <Button
                    primary
                    size="small"
                    icon
                    labelPosition="left"
                    onClick={handleExportToExcel}
                    style={{ marginRight: 'auto' }}
                >
                    <Icon name="file excel" />
                    Export to Excel
                </Button>
                {tedMonthlyTotals.map((monthTotal) => (
                    <Label key={monthTotal.monthKey} size="large" basic color="teal">
                        TED {monthTotal.label}
                        <Label.Detail>{formatCurrency(monthTotal.amount)}</Label.Detail>
                    </Label>
                ))}
                <Label size="large" color="teal">
                    TED Collections Total
                    <Label.Detail>{formatCurrency(tedTotalAmount)}</Label.Detail>
                </Label>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'scroll', position: 'relative' }}>
                <Table sortable celled fixed>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell
                                sorted={sortConfig.column === 'ProcDate' ? sortConfig.direction : null}
                                onClick={() => handleSort('ProcDate')}
                            >
                                Procedure Date
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortConfig.column === 'EntryDate' ? sortConfig.direction : null}
                                onClick={() => handleSort('EntryDate')}
                            >
                                Entry Date
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortConfig.column === 'Practice' ? sortConfig.direction : null}
                                onClick={() => handleSort('Practice')}
                            >
                                Practice
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortConfig.column === 'isTedDay' ? sortConfig.direction : null}
                                onClick={() => handleSort('isTedDay')}
                            >
                                TED
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortConfig.column === 'PatNum' ? sortConfig.direction : null}
                                onClick={() => handleSort('PatNum')}
                            >
                                Patient Number
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortConfig.column === 'Amount' ? sortConfig.direction : null}
                                onClick={() => handleSort('Amount')}
                            >
                                Amount
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {sortedRows.map((row, index) => (
                            <Table.Row key={index}>
                                <Table.Cell>{row.ProcDate}</Table.Cell>
                                <Table.Cell>{row.EntryDate}</Table.Cell>
                                <Table.Cell>{getPracticeName ? getPracticeName(row.Practice) : row.Practice}</Table.Cell>
                                <Table.Cell>{row.isTedDay ? 'Yes' : 'No'}</Table.Cell>
                                <Table.Cell>{row.PatNum}</Table.Cell>
                                <Table.Cell>{formatCurrency(row.Amount)}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>

                    {/* Totals Row */}
                    <Table.Footer>
                        <Table.Row>
                            <Table.HeaderCell />
                            <Table.HeaderCell />
                            <Table.HeaderCell />
                            <Table.HeaderCell />
                            <Table.HeaderCell />
                            <Table.HeaderCell>
                                <strong>{formatCurrency(totalAmount)}</strong>
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Footer>
                </Table>
            </div>
        </div>
    );
};

export default SortableTable;
